
import { getUsername } from "../../../constants/utility";

import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Popover,
  Typography,
  Checkbox,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import { useState, useEffect, useMemo } from "react";
import "./review.scss";
import "./retainedSheet.scss";
import { RETAINED_SHEET_MESSAGE } from "../../../constants/investigationAnalysisReview";
import ApplicationStatusChip from "../../../components/StatusChip/ApplicationStatusChip";
import { getApplicationStatusStyle } from "../../../constants/statusStyles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Label from "../../../components/Label/Label";
import Buttons from "../../../components/Controls/Buttons/Button";
import { useDispatch, useSelector } from "react-redux";
import * as types from "../../../redux/review/types";

import DatePicker from "react-datepicker";
import { format } from "date-fns";

const CHANGE_INFO_COLORS = {
  green: "#99D992",
  gray: "#C4C1C1",
  grey: "#C4C1C1",
  blue: "#A6C9FF",
};

const getChangeInfoHeaderColor = (changeInfo) => {
  if (!changeInfo) return null;

  const color = String(changeInfo.color || "").trim();
  if (!color) return null;

  return CHANGE_INFO_COLORS[color.toLowerCase()] || '';
};

const formatSpecLimit = (specLimit) => {
  if (!specLimit) return "";

  const hasLowerLimit =
    specLimit.lower_limit_type != null &&
    specLimit.lower_limit_type !== "" &&
    specLimit.lower_spec != null &&
    specLimit.lower_spec !== "";
  const hasUpperLimit =
    specLimit.upper_limit_type != null &&
    specLimit.upper_limit_type !== "" &&
    specLimit.upper_spec != null &&
    specLimit.upper_spec !== "";

  if (!hasLowerLimit && !hasUpperLimit) return "";

  const limitParts = [
    hasLowerLimit && `${specLimit.lower_limit_type} ${specLimit.lower_spec}`,
    hasUpperLimit && `${specLimit.upper_limit_type} ${specLimit.upper_spec}`,
  ].filter(Boolean);
  const specName = specLimit.spec_name ? `${specLimit.spec_name} ` : "";

  return `${specName}${limitParts.join(" And ")}`;
};

const LIMIT_COMPARATORS = {
  ">": (value, limit) => value > limit,
  ">=": (value, limit) => value >= limit,
  "≥": (value, limit) => value >= limit,
  "<": (value, limit) => value < limit,
  "<=": (value, limit) => value <= limit,
  "≤": (value, limit) => value <= limit,
  "=": (value, limit) => value === limit,
  "==": (value, limit) => value === limit,
};

const meetsLimit = (value, operator, limit) => {
  if (operator == null || operator === "" || limit == null || limit === "") {
    return true;
  }

  const comparator = LIMIT_COMPARATORS[String(operator).trim()];
  const numericLimit = Number(limit);

  return comparator && Number.isFinite(numericLimit)
    ? comparator(value, numericLimit)
    : true;
};

const isValueOutsideSpecLimit = (valueText, specLimit) => {
  if (valueText == null || String(valueText).trim() === "" || !specLimit) {
    return false;
  }

  const numericValue = Number(valueText);
  if (!Number.isFinite(numericValue)) return false;

  return !(
    meetsLimit(numericValue, specLimit.lower_limit_type, specLimit.lower_spec) &&
    meetsLimit(numericValue, specLimit.upper_limit_type, specLimit.upper_spec)
  );
};

export default function ParameterTable({
  data,
  onSaveRow,
  batchData,
  selectedReviewCheck,
  handleReviwCheckBox,
  selectedSheetCheck,
  updateData,
  onAddRow,
  addingRow,
  isFirstTab,
  returnTo,
  canEditBatchValues = false,
}) {
  const [menuState, setMenuState] = useState({ anchorEl: null, rowId: null });
  const [openDialog, setOpenDialog] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [errors, setErrors] = useState({});
  const isBatchIdColumn = (columnName) =>
    String(columnName || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "") === "batchid";
  const [anchorEl, setAnchorEl] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [localRows, setLocalRows] = useState(data?.rows || []);
  const [openAddConfirmation, setOpenAddConfirmation] = useState(false);
  const [hideGrayColumns, setHideGrayColumns] = useState(false);
  const isCopiedBatch = batchData?.is_copied_batch === true;
  const isRetainedSheet = data?.is_retained_sheet === true;

  useEffect(() => {
    setLocalRows(data?.rows || []);
  }, [data?.sheet_id, data?.rows]);

  const handleAddRow = () => {
    if (!canEditBatchValues) return;
    setOpenAddConfirmation(true);
  };

  const handleConfirmAddRow = async () => {
    if (!canEditBatchValues) return;
    try {
      await onAddRow(data?.sheet_id);
      setOpenAddConfirmation(false);
    } catch (error) {
      // The parent displays the API error notification.
    }
  };
  const dispatch = useDispatch();

  const handleEyeClick = (event, remark) => {
    setAnchorEl(event.currentTarget);
    setRemarkText(remark);
  };
  const handleEyeClose = () => {
    setAnchorEl(null);
    setRemarkText("");
  };

  const handleRowCheckbox = (sheet_row_id) => {
    if (isRetainedSheet) return;
    handleReviwCheckBox(sheet_row_id);
  };

  const handleHeaderCheckbox = (checked, sheet_id) => {
    if (isRetainedSheet) return;
    if (checked) {
      const ids = localRows.map((row) => row.sheet_row_id);
      handleReviwCheckBox(ids, sheet_id, true);
    } else {
      const ids = localRows.map((row) => row.sheet_row_id);
      handleReviwCheckBox(ids, sheet_id, false);
    }
  };

  const handleClick = (event, row) => {
    if (!canEditBatchValues || isRetainedSheet) return;
    setMenuState({ anchorEl: event.currentTarget, rowId: row.sheet_row_id });
  };
  const handleCloseMenu = () => setMenuState({ anchorEl: null, rowId: null });
  const { product, site, unitOfOperation, selectedChart, selectedTab } =
    useSelector((state) => state.shared?.productSitesUopValues || {});

  const handleEdit = (row) => {
    if (!canEditBatchValues || isRetainedSheet) return;
    const editableValues = {};
    row.values.forEach((val) => {
      if (!isBatchIdColumn(val.column_name) && !isGrayColumn(val)) {
        editableValues[`${row.sheet_row_id}-${val.column_id}`] =
          val.value_text || null;
      }
    });
    setEditValues(editableValues);
    setErrors({});
    setEditRow(row);
    setOpenDialog(true);
    handleCloseMenu();
  };

  const validateField = (val, value) => {
    let rules = [];
    if (val.validation_rule) {
      try {
        const parsed = JSON.parse(val.validation_rule);
        rules = parsed.rules || [];
      } catch (e) {
        console.error("Invalid validation_rule JSON:", val.validation_rule);
      }
    }
    if (val.is_required && (!value || value.trim() === "")) {
      return `${val.column_name} is required`;
    }
    for (const rule of rules) {
      if (rule.type === "date") {
        const datePattern = /^(0[1-9]|[12][0-9]|3[01])-[A-Z][a-z]{2}-\d{4}$/;
        if (!datePattern.test(value)) {
          return (
            rule.error_message ||
            `${val.column_name} must be in dd-MMM-yyyy format`
          );
        }
      }
      // if (rule.required && (!value || value.trim() === "")) {
      //   return rule.error_message || `${val.column_name} is required`;
      // }

      if (rule.type === "numeric" && value && isNaN(Number(value))) {
        return rule.error_message || `${val.column_name} must be numeric`;
      }

      if (rule.type === "text" && value && !/^[a-zA-Z]+$/.test(value)) {
        return rule.error_message || `${val.column_name} must be text`;
      }

      // Add more rule types as needed (min, max, regex, etc.)
    }

    return null; // no error
  };

  const handleSubmit = () => {
    if (!canEditBatchValues) return;
    const newErrors = {};
    let isValid = true;
    let batchDate = null; // 👈 store modified date

    editRow.values.forEach((val) => {
      if (isGrayColumn(val)) return;

      const fieldKey = `${editRow.sheet_row_id}-${val.column_id}`;
      const value =
        editValues[fieldKey] !== undefined
          ? editValues[fieldKey]
          : val.value_text || null;

      let error = validateField(val, value);

      if (error) {
        isValid = false;
        newErrors[fieldKey] = error;
      }

      // 👇 check if date changed
      if (
        val.data_type === "date" &&
        editValues[fieldKey] !== undefined &&
        editValues[fieldKey] !== val.value_text
      ) {
        batchDate = editValues[fieldKey];
      }
    });

    if (!editValues.update_remark || editValues.update_remark.trim() === "") {
      isValid = false;
      newErrors.update_remark = "Change Reason is required";
    }

    if (!isValid) {
      setErrors(newErrors);
      return;
    }

    if (editRow?.is_new) {
      setLocalRows((rows) =>
        rows.map((row) =>
          row.sheet_row_id === editRow.sheet_row_id
            ? {
                ...row,
                values: row.values.map((value) => ({
                  ...value,
                  value_text: isGrayColumn(value)
                    ? value.value_text
                    : (editValues[`${row.sheet_row_id}-${value.column_id}`] ??
                      value.value_text),
                })),
              }
            : row,
        ),
      );
      setOpenDialog(false);
      setEditRow(null);
      return;
    }

    const payload = {
      batch_id: batchData.batch_id,
      row_id: editRow.sheet_row_id,
      update_remark: editValues.update_remark,
      unit_op_id: unitOfOperation?.id,
      updated_by: getUsername(),

      // 👇 add batch_date only if modified
      ...(batchDate && { batch_date: batchDate }),

      values: editRow.values
        .filter(
          (val) => !isBatchIdColumn(val.column_name) && !isGrayColumn(val),
        )
        .map((val) => {
          const fieldKey = `${editRow.sheet_row_id}-${val.column_id}`;
          const newValue = editValues[fieldKey];
          const oldValue = val.value_text;

          if (newValue !== undefined && newValue !== oldValue) {
            return {
              value_id: val.value_id,
              value_text: newValue === "" ? null : newValue,
            };
          }
          return null;
        })
        .filter(Boolean),
    };

    onSaveRow(payload);
  };

  useEffect(() => {
    if (updateData?.status === 200) {
      setOpenDialog(false);
      setEditRow(null);
    }
  }, [updateData]);

  const tableColumns = useMemo(() => {
    const mostCompleteRow = localRows.reduce(
      (current, row) =>
        (row?.values?.length || 0) > (current?.values?.length || 0)
          ? row
          : current,
      null,
    );
    const columnsById = new Map();

    (mostCompleteRow?.values || []).forEach((value) => {
      columnsById.set(value.column_id, value);
    });
    localRows.forEach((row) => {
      (row?.values || []).forEach((value) => {
        if (!columnsById.has(value.column_id)) {
          columnsById.set(value.column_id, value);
        }
      });
    });

    return [...columnsById.values()];
  }, [localRows]);

  const isGrayColumn = (column) => {
    const color = String(column?.change_info?.color || "")
      .trim()
      .toLowerCase();
    return ["gray", "grey", "#edebeb", "#d8d6d6"].includes(color);
  };

  const visibleColumns = hideGrayColumns
    ? tableColumns.filter((column) => !isGrayColumn(column))
    : tableColumns;

const formatValueText = (value) => {
  if (value === null || value === undefined) return value;

  const valueString = String(value);

  if (!valueString.includes(".")) {
    return valueString;
  }

  const [integerPart, decimalPart] = valueString.split(".");

  if (decimalPart.length <= 3) {
    return valueString;
  }

  return `${integerPart}.${decimalPart.slice(0, 3)}`;
};

  return (
    <Box width="100%">
      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "calc(100vh - 230px)",
          boxShadow: "none",
          overflow: "auto",
          width: "calc(100vw - 360px)",
        }}
      >
        {(isCopiedBatch || data?.is_retained_sheet === true) && (
          <Box
            sx={{
              position: "sticky",
              left: 0,
              zIndex: 7,
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              gap: 1.25,
              minHeight: 44,
              px: 0.75,
              py: 0.75,
              pb: 1,
              borderBottom: "1px solid #DDE3EA",
              bgcolor: "#FFFFFF",
            }}
          >
            {data?.is_retained_sheet === true && (
              <Alert severity="info" className="retained-sheet-notice">
                {RETAINED_SHEET_MESSAGE}
              </Alert>
            )}
            {isCopiedBatch && data?.is_retained_sheet !== true && <Box
              component="label"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                height: 34,
                pl: 0.75,
                pr: 1,
                border: "1px solid",
                borderColor: hideGrayColumns ? "#84ADFF" : "#D0D5DD",
                borderRadius: "8px",
                bgcolor: hideGrayColumns ? "#EFF4FF" : "#FFFFFF",
                cursor: "pointer",
                userSelect: "none",
                transition:
                  "background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
                "&:hover": {
                  borderColor: "#84ADFF",
                  boxShadow: "0 1px 3px rgba(16, 24, 40, 0.12)",
                },
              }}
            >
              <Checkbox
                size="small"
                checked={hideGrayColumns}
                onChange={(event) => setHideGrayColumns(event.target.checked)}
                sx={{
                  p: 0.25,
                  color: "#667085",
                  "& .MuiSvgIcon-root": { fontSize: 20 },
                  "&.Mui-checked": { color: "#175CD3" },
                }}
              />
              <Typography
                fontSize={14}
                fontWeight={600}
                color="#344054"
                whiteSpace="nowrap"
              >
                Show/Hide Retained Columns
              </Typography>
              <Box
                component="span"
                sx={{
                  ml: 0.25,
                  px: 0.75,
                  py: 0.15,
                  borderRadius: "10px",
                  bgcolor: hideGrayColumns ? "#D1E0FF" : "#F2F4F7",
                  color: hideGrayColumns ? "#1849A9" : "#475467",
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.5,
                }}
              >
                {hideGrayColumns ? "Hidden" : "Visible"}
              </Box>
            </Box>}
            {canEditBatchValues && isCopiedBatch && data?.is_retained_sheet !== true && !isFirstTab && (
              <Button
                variant="contained"
                size="small"
                aria-label="Add row"
                startIcon={<AddIcon />}
                onClick={handleAddRow}
                disabled={addingRow}
                sx={{
                  minWidth: 105,
                  height: 34,
                  px: 1.5,
                  borderRadius: "6px",
                  textTransform: "none",
                  boxShadow: "none",
                  fontSize: 14,
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Add Row
              </Button>
            )}
          </Box>
        )}
        <Table
          stickyHeader
          sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}
        >
          <TableHead className="table-header">
            <TableRow>
              {!isRetainedSheet && <TableCell
                className="table-header-cell"
                sx={{
                  overflow: "visible",
                  zIndex: 6,
                  width: 56,
                  minWidth: 56,
                  height: 48,
                  padding: "0 !important",
                  textAlign: "center",
                  verticalAlign: "middle",
                }}
              >
                <Checkbox
                  checked={selectedSheetCheck?.includes(data?.sheet_id)}
                  disabled={isRetainedSheet}
                  onChange={(e) =>
                    handleHeaderCheckbox(e.target.checked, data?.sheet_id)
                  }
                  size="small"
                />
              </TableCell>}
              {visibleColumns.map((val) => {
                const specLimitText = formatSpecLimit(val?.spec_limit);

                return (
                  <Tooltip
                    key={val.column_id}
                    title={val?.change_info?.message || ""}
                    placement="top"
                    arrow
                  >
                    <TableCell
                      className="table-header-cell"
                      sx={{
                        whiteSpace: "nowrap",
                        verticalAlign: "top !important",
                        ...(getChangeInfoHeaderColor(val?.change_info) && {
                          backgroundColor: `${getChangeInfoHeaderColor(val.change_info)} !important`,
                        }),
                        ...(val?.change_info?.message && {
                          cursor: "help",
                        }),
                      }}
                    >
                      <Typography
                        component="div"
                        fontSize="inherit"
                        fontWeight="inherit"
                        whiteSpace="nowrap"
                      >
                        {val.column_name}
                      </Typography>
                      {specLimitText && (
                        <Typography
                          component="div"
                          variant="caption"
                          sx={{
                            mt: 0.5,
                            fontSize: "12px",
                            color: "text.secondary",
                            lineHeight: 1.3,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {specLimitText}
                        </Typography>
                      )}
                    </TableCell>
                  </Tooltip>
                );
              })}
              <TableCell
                className="table-header-cell"
                sx={{
                  paddingLeft: "40px !important",
                  whiteSpace: "nowrap",
                  verticalAlign: "top !important",
                }}
              >
                Status
              </TableCell>
              {!isRetainedSheet && <TableCell
                className="table-header-cell"
                sx={{
                  textAlign: "center",
                  minWidth: 120,
                  width: 120,
                  whiteSpace: "nowrap",
                  verticalAlign: "top !important",
                }}
              >
                Actions
              </TableCell>}
            </TableRow>
          </TableHead>
          <Divider sx={{ border: "none", height: "2px" }} />
          <TableBody
            sx={{
              "& .MuiTableRow-root": { height: 48 },
              "& .MuiTableCell-root": { py: 0.5 },
            }}
          >
            {localRows.map((row) => (
              <TableRow
                key={row.sheet_row_id}
                sx={{
                  backgroundColor:
                    editRow?.sheet_row_id === row?.sheet_row_id
                      ? "#D5D7DA"
                      : "",
                }}
              >
                {!isRetainedSheet && <TableCell
                  sx={{
                    width: 56,
                    minWidth: 56,
                    height: 48,
                    padding: "0 !important",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  {row.status !== "Approved" ? (
                    <>
                      <Checkbox
                        checked={selectedReviewCheck?.includes(
                          row.sheet_row_id,
                        )}
                        disabled={isRetainedSheet}
                        onChange={() => handleRowCheckbox(row.sheet_row_id)}
                        size="small"
                      />
                    </>
                  ) : (
                    ""
                  )}
                </TableCell>}
                {visibleColumns.map((column) => {
                  const value = row?.values?.find(
                    (item) => item.column_id === column.column_id,
                  );
                  const isOutsideLimit = isValueOutsideSpecLimit(
                    value?.value_text,
                    column?.spec_limit,
                  );
                  return (
                    <TableCell
                      sx={{
                        fontSize: 14,
                        whiteSpace: "nowrap",
                        ...(isOutsideLimit && {
                          backgroundColor: "#FECACA",
                          color: "#991B1B",
                        }),
                      }}
                      key={`${row.sheet_row_id}-${column.column_id}`}
                    >
                      {formatValueText(value?.value_text) ?? "-"}
                    </TableCell>
                  );
                })}
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {" "}
                  {getApplicationStatusStyle(row.status) ? (
                    <ApplicationStatusChip status={row.status} sx={{ minWidth: 120 }} />
                  ) : (
                    <Chip
                      label={row.status}
                      className="review-status-chip status-chip status-chip-color-gray"
                    />
                  )}
                </TableCell>
                {!isRetainedSheet && <TableCell
                  sx={{ minWidth: 120, width: 120, whiteSpace: "nowrap" }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "32px 32px",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.25,
                    }}
                  >
                    <IconButton
                      size="small"
                      disabled={
                        !canEditBatchValues ||
                        isRetainedSheet ||
                        !row?.is_editable ||
                        returnTo
                      }
                      onClick={(e) => handleClick(e, row)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    {canEditBatchValues && <Menu
                      anchorEl={menuState.anchorEl}
                      open={menuState.rowId === row.sheet_row_id}
                      onClose={handleCloseMenu}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      MenuListProps={{
                        sx: {
                          padding: 0,
                          backgroundColor: "white",
                          borderRadius: 0,
                        },
                      }}
                    >
                      <MenuItem disabled={isRetainedSheet} onClick={() => handleEdit(row)}>Edit</MenuItem>
                    </Menu>}
                    {row.update_remark && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleEyeClick(e, row.update_remark)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>}
              </TableRow>
            ))}
            {!data?.loader && localRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + (isRetainedSheet ? 1 : 3)} align="center">
                  <Label color="textSecondary" text={"No data found"} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog
        PaperProps={{
          sx: {
            margin: 0,
            width: 450,
            minHeight: 550,
            overflow: "auto",
            position: "fixed",
            bottom: 0,
            right: 0,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: 0.5,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "transparent",
            backdropFilter: "none",
          },
        }}
        open={canEditBatchValues && openDialog}
        onClose={() => {
          setOpenDialog(false);
          // dispatch({ type: types.CLEAR_UPDATE_MESSAGE });
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editRow?.is_new ? "Add Row" : "Edit"}</DialogTitle>
        <DialogContent dividers>
          {editRow?.values?.map((val) => {
            const fieldKey = `${editRow.sheet_row_id}-${val.column_id}`;
            const isBatchId = isBatchIdColumn(val.column_name);
            const isEditable = !isBatchId && !isGrayColumn(val);
            const value = isBatchId
              ? batchData?.batch_id || val.value_text || ""
              : (editValues[fieldKey] ?? val.value_text);
            if (val.data_type === "date") {
              return (
                <Box sx={{ width: "100%" }}>
                  <DatePicker
                    key={fieldKey}
                    selected={value ? new Date(value) : null}
                    showYearDropdown
                    scrollableYearDropdown
                    wrapperClassName="w-full"
                    yearDropdownItemNumber={150}
                    dateFormat="dd-MMM-yyyy"
                    onChange={(date) =>
                      isEditable &&
                      setEditValues((prev) => ({
                        ...prev,
                        [fieldKey]: date ? format(date, "dd-MMM-yyyy") : "",
                      }))
                    }
                    customInput={
                      <TextField
                        fullWidth
                        size="small"
                        margin="dense"
                        label={
                          val.is_required
                            ? `${val.column_name} *`
                            : val.column_name
                        }
                        error={!!errors[fieldKey]}
                        helperText={errors[fieldKey] || ""}
                        disabled={!isEditable}
                        InputLabelProps={{ shrink: true }}
                        style={{ width: "100%" }}
                      />
                    }
                  />
                </Box>
              );
            }
            return (
              <TextField
                key={fieldKey}
                fullWidth
                size="small"
                margin="dense"
                label={
                  val.is_required ? `${val.column_name} *` : val.column_name
                }
                value={value}
                onChange={(e) =>
                  isEditable &&
                  setEditValues((prev) => ({
                    ...prev,
                    [fieldKey]: e.target.value,
                  }))
                }
                error={!!errors[fieldKey]}
                helperText={errors[fieldKey] || ""}
                disabled={!isEditable}
              />
            );
          })}

          {/* Change Reason */}
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Change Reason"
            value={editValues.update_remark || ""}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                update_remark: e.target.value,
              }))
            }
            error={!!errors.update_remark}
            helperText={errors.update_remark || ""}
            required
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setEditRow(null);
            }}
          >
            Cancel
          </Button>
          <Buttons
            loading={updateData?.loader}
            onClick={handleSubmit}
            text={"Save"}
            variant="contained"
          ></Buttons>
        </DialogActions>
      </Dialog>
      <Dialog
        open={canEditBatchValues && openAddConfirmation}
        onClose={addingRow ? undefined : () => setOpenAddConfirmation(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="add-row-confirmation-title"
      >
        <DialogTitle id="add-row-confirmation-title">
          Confirm Add Row
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to add a blank row to this tab?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenAddConfirmation(false)}
            disabled={addingRow}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAddRow}
            disabled={addingRow}
          >
            {addingRow ? "Adding..." : "Add Row"}
          </Button>
        </DialogActions>
      </Dialog>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleEyeClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { p: 1.5, maxWidth: 250, borderRadius: 2, boxShadow: 3 },
        }}
      >
        <Typography variant="body2">{remarkText}</Typography>
      </Popover>
    </Box>
  );
}
